import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { FAQContentRenderer } from '../components/faq/FAQContentRenderer';
import { getFAQByPath } from '@/config/faqs.config';
import { createFAQContext } from '@/utils/faqToText';
import { fbPixelService } from '@/services/retargeting/fb.pixel.service';

interface FAQPageProps {
    showBanner: () => void;
}

export const FAQPage: React.FC<FAQPageProps> = ({ showBanner }) => {
    const { '*': faqPath } = useParams();
    const navigate = useNavigate();
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat(faqPath);
    const faq = getFAQByPath(`/faq/${faqPath}`);

    // Inject hidden FAQ context on the first message so the assistant has full topic context
    const sendMessageWithContext = useCallback((userMessage: string) => {
        const hiddenContext = faq && messages.length === 0 ? createFAQContext(faq, true) : undefined;
        sendMessage(userMessage, hiddenContext);
    }, [faq, messages.length, sendMessage]);

    // Fire ViewContent pixel when a valid FAQ page loads
    useEffect(() => {
        if (faq) fbPixelService.trackViewContent();
    }, [faq]);

    // Loading state
    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <LoadingState message="Loading..." />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // FAQ not found
    if (!faq) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center text-white">
                            <h1 className="text-2xl font-bold mb-4">FAQ Not Found</h1>
                            <p className="text-gray-400 mb-6">The FAQ you're looking for doesn't exist.</p>
                            <button
                                onClick={() => navigate('/')}
                                className="px-6 py-2 bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all"
                            >
                                Go Home
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // FAQ page with prepopulated content and chat
    const faqSchema = faq ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            ...(faq.sections[0]?.content ? [{
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.sections[0].content,
                },
            }] : []),
            ...faq.sections.slice(1)
                .filter(s => s.content)
                .map(s => ({
                    '@type': 'Question',
                    name: s.heading,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: s.content as string,
                    },
                })),
        ],
    } : null;

    return (
        <>
            {faq && faqSchema && <SchemaMarkup schema={faqSchema} />}
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        {/* Prepopulated FAQ Content */}
                        <FAQContentRenderer faq={faq} onSendMessage={sendMessageWithContext} assistant={assistant} />

                        {/* Follow-up Chat Messages */}
                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessageWithContext}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
